import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { Alert, Button, Col, Row, Spinner } from 'react-bootstrap'

import * as FormWizardStyles from '../../../../components/organisms/FormWizard/styles'
import { FormWizardHandlers } from '../../../../components/organisms/FormWizard'
import { CreateContractsContext } from '../index'
import { AppContext } from '../../../../app'
import { useToast } from '../../../../hooks/useToast'
import { useCreationConfirmModal } from '../../../../hooks/useCreationConfirmModal'
import { useContextPage } from '../contextPage'
import { Contract } from '../../../../domain/usecases/Contracts'
import { previewMinuta, getEditedMinuta } from '../../../../services/contracts'

import * as S from './styles'

export type ICreateContractsFormStep7c = Pick<
  Contract.CreateParams,
  'edited_minuta_html'
>

const MINUTA_LABELS: Record<string, string> = {
  'agreement.pug': 'Minuta padrão',
  'wellness4-charitas.pug': 'Minuta Charitas',
  'quadro-resumo.pug': 'Quadro resumo'
}

const LOSS_THRESHOLD = 0.6

export default function Step7c({
  nextHandler,
  backHandler
}: FormWizardHandlers) {
  const { data, setData, isEditMode, contractId } = useContext(
    CreateContractsContext
  )
  const { user } = useContext(AppContext)
  const { state } = useContextPage()
  const { showToast } = useToast()

  const [html, setHtml] = useState('')
  const [minutaFile, setMinutaFile] = useState('')
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [hasEdits, setHasEdits] = useState(!!data?.edited_minuta_html)
  const [reloadKey, setReloadKey] = useState(0)

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const templateHtmlRef = useRef('')

  const { setShowConfirmationModal, Modal } = useCreationConfirmModal({
    entityText: 'do Contrato',
    onFinish: nextHandler
  })

  const buildDraft = useCallback(() => {
    const toIso = (date: any) =>
      date ? new Date(String(date)).toISOString() : undefined

    return {
      company_id: data?.company_id,
      spe_id: data?.spe_id,
      project_id: data?.project_id,
      unit_id: data?.unit_id,
      buyers: data?.buyers ?? [],
      value: Number(data?.value),
      discount_rate: Number(data?.discount_rate) || 0,
      billing_person_id: data?.billing_person_id,
      company_is_signing: !!data?.company_is_signing,
      deadline_allows_modify_unit: toIso(data?.deadline_allows_modify_unit),
      // não era persistido, então o flag pode não existir no data.
      has_custom_contract:
        !!data?.custom_contract || !!data?.custom_contract_file,
      payment_parts: (data?.payment_parts ?? []).map((part: any) => ({
        name: part.name,
        frequency: part.frequency,
        installments: Number(part.installments),
        installment_value: Number(part.installment_value),
        total_value: Number(part.total_value),
        first_due_date: toIso(part.first_due_date)
      }))
    }
  }, [data])

  const fetchPreview = useCallback(async () => {
    setLoading(true)
    setErrorMsg('')
    try {
      const response: any = await previewMinuta(
        buildDraft(),
        user!.token,
        user!.selectedContextEncoded
      )
      const received = typeof response === 'string' ? response : response?.html
      if (!received) throw new Error('resposta sem HTML')

      setHtml(received)
      templateHtmlRef.current = received
      setMinutaFile(typeof response === 'string' ? '' : response?.minuta ?? '')
      setReloadKey((key) => key + 1)
    } catch (error: any) {
      const detail =
        error?.response?.data?.message ??
        error?.message ??
        'erro não identificado'
      setErrorMsg(
        `Não foi possível gerar a minuta: ${detail}. Volte um passo e confira os dados do Contrato.`
      )
    } finally {
      setLoading(false)
    }
  }, [buildDraft, user])

  useEffect(() => {
    if (data?.edited_minuta_html) {
      setHtml(data.edited_minuta_html)
      templateHtmlRef.current = ''
      setHasEdits(true)
      setLoading(false)
      return
    }

    if (!isEditMode || !contractId) {
      fetchPreview()
      return
    }

    let cancelled = false

    ;(async () => {
      try {
        const saved = await getEditedMinuta(
          contractId,
          user!.token,
          user!.selectedContextEncoded
        )
        if (cancelled) return

        if (saved) {
          setHtml(saved)
          templateHtmlRef.current = ''
          setHasEdits(true)
          setLoading(false)
          setData?.({ ...data, edited_minuta_html: saved })
          return
        }
      } catch {
        // erros: seguem para o preview normal nao sei o que colocar aquio
      }
      if (!cancelled) fetchPreview()
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const startEditing = () => {
    const doc = iframeRef.current?.contentDocument
    if (!doc?.body) {
      return showToast('A minuta ainda está carregando. Aguarde.', 'error')
    }
    doc.body.contentEditable = 'true'
    doc.body.style.outline = 'none'
    doc.body.focus()
    setIsEditing(true)
  }

  const saveEdits = () => {
    const doc = iframeRef.current?.contentDocument
    if (!doc?.documentElement) return

    doc.body.contentEditable = 'false'
    const captured = `<!DOCTYPE html>${doc.documentElement.outerHTML}`
    const original = templateHtmlRef.current || html

    const keepEditing = () => {
      doc.body.contentEditable = 'true'
    }

    if (!captured.includes('<style')) {
      keepEditing()
      return showToast(
        'A minuta ficou sem os estilos. Descarte as alterações e edite de novo sem apagar o topo do documento.',
        'error'
      )
    }

    if (original && captured.length < original.length * LOSS_THRESHOLD) {
      const confirmed = window.confirm(
        'A minuta editada está bem menor que a original. Isso costuma indicar que um trecho foi apagado sem intenção. Salvar assim mesmo?'
      )
      if (!confirmed) return keepEditing()
    }

    setData?.({ ...data, edited_minuta_html: captured })
    setHtml(captured)
    setIsEditing(false)
    setHasEdits(true)
    showToast(
      'Alterações salvas. O PDF do Contrato usará esta versão.',
      'success'
    )
  }

  const discardEdits = async () => {
    const confirmed = window.confirm(
      'Descartar as alterações e voltar à minuta gerada pelo sistema?'
    )
    if (!confirmed) return

    const doc = iframeRef.current?.contentDocument
    if (doc?.body) doc.body.contentEditable = 'false'

    // String vazia, não `null`: o serializador do FormData descarta valores
    // nulos, então o campo nunca chegava ao backend e a minuta editada antiga
    // seguia salva. Com '', o backend normaliza para null e limpa a coluna.
    setData?.({ ...data, edited_minuta_html: '' })
    setIsEditing(false)
    setHasEdits(false)
    await fetchPreview()
  }

  const onClickNext = () => {
    if (isEditing) {
      return showToast(
        'Salve ou descarte as alterações antes de continuar.',
        'error'
      )
    }
    setShowConfirmationModal(true)
  }

  return (
    <>
      <FormWizardStyles.TabContent>
        <Row>
          <Col md={12}>
            <Alert variant="info">
              <strong>Revise a minuta antes de criar o Contrato.</strong>
              <br />
              Se você editar o documento, o PDF enviado para assinatura usará
              exatamente o texto salvo aqui — o modelo do sistema deixa de ser
              aplicado.
            </Alert>
          </Col>
        </Row>

        <Row>
          <Col md={8}>
            {state?.projectsSelected?.name && (
              <span style={{ fontWeight: 500 }}>
                {state.projectsSelected.name}
                {state?.unitSelected?.name && (
                  <>
                    {' · '}
                    <strong>
                      {state.unitSelected.building?.name} -{' '}
                      {state.unitSelected.name}
                    </strong>
                  </>
                )}
              </span>
            )}
          </Col>
          <Col md={4}>
            <S.BadgesWrapper>
              {!!minutaFile && (
                <label className="badge badge-light-primary">
                  {MINUTA_LABELS[minutaFile] ?? minutaFile}
                </label>
              )}
              {hasEdits && (
                <label className="badge badge-light-danger">
                  Minuta editada
                </label>
              )}
            </S.BadgesWrapper>
          </Col>
        </Row>

        {!!errorMsg && (
          <Row>
            <Col md={12}>
              <Alert
                variant="danger"
                onClose={() => setErrorMsg('')}
                dismissible
              >
                {errorMsg}
              </Alert>
            </Col>
          </Row>
        )}

        {isEditing && (
          <Row>
            <Col md={12}>
              <Alert variant="warning">
                Edição ativada. Clique no texto para alterar. Não apague os
                blocos de assinatura no fim do documento.
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="mb-3">
          <Col md={12}>
            <S.ActionsWrapper>
              {isEditing ? (
                <Button variant="primary" onClick={saveEdits}>
                  Salvar alterações
                </Button>
              ) : (
                <Button
                  variant="outline-primary"
                  onClick={startEditing}
                  disabled={loading || !html}
                >
                  Editar minuta
                </Button>
              )}

              {hasEdits && (
                <Button
                  variant="outline-danger"
                  onClick={discardEdits}
                  disabled={loading}
                >
                  Descartar alterações
                </Button>
              )}

              {!hasEdits && !isEditing && (
                <Button
                  variant="outline-secondary"
                  onClick={fetchPreview}
                  disabled={loading}
                >
                  Recarregar minuta
                </Button>
              )}
            </S.ActionsWrapper>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <S.PreviewFrame>
              {loading && (
                <S.FrameState>
                  <Spinner animation="border" variant="primary" />
                </S.FrameState>
              )}

              {!loading && !!html && (
                <iframe
                  key={reloadKey}
                  ref={iframeRef}
                  title="Minuta do Contrato"
                  // srcDoc herda a origem da aplicação, então um <script> na
                  // minuta rodaria com acesso ao localStorage (onde fica o
                  // token). Sem `allow-scripts` nada executa; com
                  // `allow-same-origin` o contentDocument segue acessível,
                  // que é do que o contentEditable precisa.
                  sandbox="allow-same-origin"
                  srcDoc={html}
                />
              )}

              {!loading && !html && !errorMsg && (
                <S.FrameState>Nenhuma minuta carregada.</S.FrameState>
              )}
            </S.PreviewFrame>
          </Col>
        </Row>

        <Row>
          <Col>
            <FormWizardStyles.ButtonsWrapperSpaced>
              <Button variant="link" onClick={backHandler} disabled={isEditing}>
                Voltar
              </Button>
              <Button
                variant="primary"
                onClick={onClickNext}
                disabled={loading || !html}
              >
                {isEditMode ? 'Atualizar Contrato' : 'Criar Contrato'}
              </Button>
            </FormWizardStyles.ButtonsWrapperSpaced>
          </Col>
        </Row>
      </FormWizardStyles.TabContent>
      {Modal}
    </>
  )
}
